/**
 * AI difficulty ladder — Easy / Advanced / Expert.
 *
 * Tuning constants (not game data): the AI director scales its economy,
 * construction, research, training, and aggression from these. Expert is
 * strictly stronger than Advanced, which is strictly stronger than Easy,
 * on every aggression/economy axis (smaller timings = more aggressive).
 * Render-free: no three.js, no Math.random.
 */

export type DifficultyId = "easy" | "advanced" | "expert";

export const DIFFICULTY_IDS: DifficultyId[] = ["easy", "advanced", "expert"];

export interface CompWeights {
  infantry: number;
  skirmisher: number;
  cavalry: number;
}

export interface DifficultyConfig {
  /** Display label for the setup screen and HUD. */
  label: string;
  /** Worker gather/haul rate multiplier (AI economy). */
  gatherRateMult: number;
  /** Construction speed multiplier for AI buildings. */
  buildSpeedMult: number;
  /** Research speed multiplier. */
  researchSpeedMult: number;
  /** Training speed multiplier. */
  trainSpeedMult: number;
  /**
   * AI logistics handicap: multiplies the upkeep the AI pays for its
   * forces. Lower = stronger AI. Data upkeep (0.06 food/s per soldier)
   * makes full-price warbands unsustainable for anyone, so the director
   * needs this lever to field armies at all; higher difficulties run
   * leaner logistics.
   */
  upkeepMult: number;
  /** Desired worker count (economy size). */
  workerTarget: number;
  /** Cap on standing military combatants (warbands). */
  maxWarbands: number;
  /** 0..1 — probability a production tick favors military over workers. */
  militarySpendBias: number;
  /** Idle guard posts around the camp. */
  guardPosts: number;
  /** Camp defense: chase intruders within this range. */
  aggroRange: number;
  /** Camp defense: break off pursuit beyond this range. */
  leashRange: number;
  /** Seconds between scout waypoint reassignments. */
  scoutInterval: number;
  /** Max simultaneous scouts sweeping for the player base. */
  maxScouts: number;
  /** Waypoint jitter around the ±95 quadrant centers (map coverage). */
  scoutWaypointJitter: number;
  /** Sim time of the first wave (once the player base is known). */
  firstWaveAt: number;
  /** Seconds between waves — pressure repeats, never one-shot. */
  waveInterval: number;
  /** Warbands committed to an assault wave. */
  waveSize: number;
  /** Warbands committed to a raid wave. */
  raidSize: number;
  /** Sim time after which waves may target the command building. */
  allOutAt: number;
  /** Building-id substrings matched data-driven for camp defense. */
  defensiveBuildings: string[];
  /** Desired military composition when training. */
  compWeights: CompWeights;
  /**
   * PROTOTYPE TUNING: AI organization-drill cadence (sim/organization.ts).
   * The AI "drills in the field": it abstracts the canonical building /
   * echelon / research-site prerequisites of its org techs (E2+ drill
   * grounds the prototype AI never constructs) and follows ORG_TECH_LINE
   * in order on a per-difficulty time cadence instead. The full research
   * cost is still paid and the line order is still honored. Higher
   * difficulties drill earlier, more often, deeper, and from a smaller
   * food reserve.
   */
  /** How deep into the faction's ORG_TECH_LINE the AI drills. */
  orgMaxTechs: number;
  /** Sim seconds before the first org drill may start. */
  orgStartAt: number;
  /** Minimum sim seconds between org tech starts (echelon-cadence proxy). */
  orgInterval: number;
  /** Food stock must exceed this to start an org tech (drills from surplus). */
  orgReserveFood: number;
}

export const DIFFICULTIES: Record<DifficultyId, DifficultyConfig> = {
  easy: {
    label: "Easy",
    gatherRateMult: 0.85,
    buildSpeedMult: 0.9,
    researchSpeedMult: 0.8,
    trainSpeedMult: 0.85,
    upkeepMult: 0.5,
    workerTarget: 5,
    maxWarbands: 4,
    militarySpendBias: 0.35,
    guardPosts: 3,
    aggroRange: 35,
    leashRange: 70,
    scoutInterval: 45,
    maxScouts: 1,
    scoutWaypointJitter: 40,
    firstWaveAt: 420,
    waveInterval: 240,
    waveSize: 2,
    raidSize: 1,
    // Easy never assaults the command building — raids only.
    allOutAt: Infinity,
    defensiveBuildings: ["palisade", "watch", "wall", "rampart", "gate"],
    compWeights: { infantry: 0.6, skirmisher: 0.3, cavalry: 0.1 },
    // PROTOTYPE TUNING: Easy drills one org tier, late and cautiously.
    orgMaxTechs: 1,
    orgStartAt: 540,
    orgInterval: 600,
    orgReserveFood: 150,
  },
  advanced: {
    label: "Advanced",
    gatherRateMult: 1.0,
    buildSpeedMult: 1.0,
    researchSpeedMult: 1.0,
    trainSpeedMult: 1.0,
    upkeepMult: 0.4,
    workerTarget: 7,
    maxWarbands: 6,
    militarySpendBias: 0.55,
    guardPosts: 4,
    aggroRange: 45,
    leashRange: 90,
    scoutInterval: 30,
    maxScouts: 2,
    scoutWaypointJitter: 55,
    firstWaveAt: 300,
    waveInterval: 180,
    waveSize: 3,
    raidSize: 3,
    allOutAt: 600,
    defensiveBuildings: ["palisade", "watch", "wall", "rampart", "gate"],
    compWeights: { infantry: 0.5, skirmisher: 0.3, cavalry: 0.2 },
    // PROTOTYPE TUNING: Advanced drills two org tiers on a mid-game cadence.
    orgMaxTechs: 2,
    orgStartAt: 360,
    orgInterval: 450,
    orgReserveFood: 100,
  },
  expert: {
    label: "Expert",
    gatherRateMult: 1.2,
    buildSpeedMult: 1.25,
    researchSpeedMult: 1.3,
    trainSpeedMult: 1.25,
    upkeepMult: 0.3,
    workerTarget: 9,
    maxWarbands: 8,
    militarySpendBias: 0.7,
    guardPosts: 5,
    aggroRange: 55,
    leashRange: 110,
    scoutInterval: 20,
    maxScouts: 3,
    scoutWaypointJitter: 70,
    firstWaveAt: 210,
    waveInterval: 135,
    waveSize: 4,
    raidSize: 4,
    allOutAt: 420,
    defensiveBuildings: ["palisade", "watch", "wall", "rampart", "gate"],
    compWeights: { infantry: 0.45, skirmisher: 0.3, cavalry: 0.25 },
    // PROTOTYPE TUNING: Expert drills the whole org line aggressively.
    orgMaxTechs: 99,
    orgStartAt: 240,
    orgInterval: 300,
    orgReserveFood: 50,
  },
};
